import { defineStore } from 'pinia'
import { ref, computed, watch } from 'vue'
import { useAuthStore } from './auth.js'
import { useOrgStore } from './orgStore.js'
import { assertPermission, RBAC_ERROR } from '../utils/permissions.js'
import {
  assignManagersToLotteryRequest,
  archiveLotteryRequest,
  closeLotteryRequest,
  assignUsersToLotteryRequest,
  createLotteryRequest,
  drawWinnerRequest,
  fetchLotteryDetailRequest,
  fetchBatchByTicketRequest,
  fetchBatchImageRequest,
  fetchBatchDetailRequest,
  fetchDrawHistoryRequest,
  fetchLotteriesRequest,
  fetchLotteryBatchesRequest,
  fetchMyBatchesRequest,
  fetchUserBatchesRequest,
  uploadTicketBatchRequest,
  fetchLotteryAssignmentsRequest,
  pauseLotteryRequest,
  resumeLotteryRequest,
  updateLotteryRequest,
} from '../services/lotteryService.js'

export const useLotteryStore = defineStore('lottery', () => {
  const authStore = useAuthStore()
  const orgStore = useOrgStore()
  const lotteries = ref([])
  const accessibleLotteryIds = ref([])
  const currentLottery = ref(null)
  const assignments = ref([])
  const batches = ref([])
  const drawHistory = ref([])
  const drawLocks = ref({})
  const assignmentLocks = ref({})
  const loading = ref(false)
  const error = ref('')
  let requestToken = 0

  const getLotteryId = (lottery) => lottery?.id ?? lottery?.lottery_id ?? null

  const normalizeLotteryPayload = (lottery) => {
    if (!lottery || typeof lottery !== 'object') return lottery
    const ticketPriceRaw = lottery?.ticket_price ?? lottery?.price_per_ticket ?? lottery?.unit_price
    return {
      ...lottery,
      ...(ticketPriceRaw !== undefined && ticketPriceRaw !== null
        ? { ticket_price: String(ticketPriceRaw) }
        : {}),
    }
  }

  const normalizeLotteries = (data) => {
    if (Array.isArray(data)) return data.map(normalizeLotteryPayload)
    if (Array.isArray(data?.items)) return data.items.map(normalizeLotteryPayload)
    if (Array.isArray(data?.lotteries)) return data.lotteries.map(normalizeLotteryPayload)
    return []
  }

  const syncLotteryInState = (payload) => {
    if (!payload || typeof payload !== 'object') return
    const normalizedPayload = normalizeLotteryPayload(payload)
    const payloadId = String(getLotteryId(normalizedPayload))
    if (!payloadId) return

    lotteries.value = lotteries.value.map((lottery) => {
      const lotteryId = String(getLotteryId(lottery))
      if (lotteryId !== payloadId) return lottery
      return normalizeLotteryPayload({
        ...lottery,
        ...normalizedPayload,
      })
    })

    if (String(getLotteryId(currentLottery.value)) === payloadId) {
      currentLottery.value = normalizeLotteryPayload({
        ...currentLottery.value,
        ...normalizedPayload,
      })
    }
  }

  const accessibleLotteryIdSet = computed(() => new Set(accessibleLotteryIds.value.map(String)))

  const accessibleLotteries = computed(() => lotteries.value)

  const filteredLotteries = computed(() => lotteries.value)

  const canAccessLottery = (id) => {
    return accessibleLotteryIdSet.value.has(String(id))
  }

  const resolveAccessibleLotteryIds = async (list) => {
    accessibleLotteryIds.value = list
      .map((lottery) => getLotteryId(lottery))
      .filter((id) => id !== null && id !== undefined)
      .map(String)
  }

  const userRole = computed(() => {
    // Derive role from assignments endpoint: if user is in assignments, they're a user; otherwise manager
    if (!assignments.value.length) return 'manager' // First time manager
    const isInAssignments = assignments.value.some((a) => a.user_id !== undefined)
    return isInAssignments ? 'user' : 'manager'
  })

  const resetLotteriesState = () => {
    lotteries.value = []
    accessibleLotteryIds.value = []
    currentLottery.value = null
    drawLocks.value = {}
    error.value = ''
    loading.value = false
  }

  const toLockKey = (lotteryId) => String(lotteryId ?? '')

  const isDrawLocked = (lotteryId) => {
    const key = toLockKey(lotteryId)
    return Boolean(key && drawLocks.value[key])
  }

  const setDrawLocked = (lotteryId, locked) => {
    const key = toLockKey(lotteryId)
    if (!key) return
    drawLocks.value = {
      ...drawLocks.value,
      [key]: Boolean(locked),
    }
  }

  const toAssignmentLockKey = (lotteryId, scope = 'users') => `${String(lotteryId ?? '')}:${String(scope || 'users')}`

  const isAssignmentLocked = (lotteryId, scope = 'users') => {
    const key = toAssignmentLockKey(lotteryId, scope)
    return Boolean(key && assignmentLocks.value[key])
  }

  const setAssignmentLocked = (lotteryId, scope = 'users', locked) => {
    const key = toAssignmentLockKey(lotteryId, scope)
    if (!key) return
    assignmentLocks.value = {
      ...assignmentLocks.value,
      [key]: Boolean(locked),
    }
  }

  const isAssignmentBlockedByLifecycle = (lotteryId) => {
    const activeLottery = lotteries.value.find((item) => String(getLotteryId(item)) === String(lotteryId))
      ?? (String(getLotteryId(currentLottery.value)) === String(lotteryId) ? currentLottery.value : null)
    const status = String(activeLottery?.status ?? '').trim().toLowerCase()
    return status === 'closed' || status === 'drawn' || status === 'archived'
  }

  const toActionResult = (err, fallbackMessage) => {
    if (err?.code === RBAC_ERROR) {
      error.value = 'Access denied'
      return { success: false, error: error.value, code: RBAC_ERROR }
    }

    error.value = err?.response?.data?.detail ?? fallbackMessage
    return { success: false, error: error.value }
  }

  const rbacDeniedResult = () => ({ success: false, error: 'Access denied', code: RBAC_ERROR })

  const fetchLotteries = async () => {
    if (!orgStore.isOrgReady) return

    if (authStore.contextReady && !orgStore.activeSubOrgId) {
      console.warn('[lottery] blocked unscoped fetch after contextReady')
      return
    }

    const subOrgId = orgStore.activeSubOrgId

    const token = ++requestToken

    loading.value = true
    error.value = ''

    try {
      assertPermission(authStore, orgStore, 'viewLotteries', { scope: 'org' })
      const data = await fetchLotteriesRequest(orgStore.activeOrgId, subOrgId ?? null)
      if (token !== requestToken) return

      lotteries.value = normalizeLotteries(data)
      await resolveAccessibleLotteryIds(lotteries.value)

      if (token !== requestToken) return
      return { success: true, data: lotteries.value }
    } catch (err) {
      if (token !== requestToken) return

      if (err?.code === RBAC_ERROR) {
        lotteries.value = []
        accessibleLotteryIds.value = []
        error.value = 'Access denied'
        return { success: false, error: error.value, code: RBAC_ERROR }
      }

      console.error('Failed to fetch lotteries', err)
      lotteries.value = []
      accessibleLotteryIds.value = []
      error.value = err?.response?.data?.detail ?? 'Failed to fetch lotteries'
      return { success: false, error: error.value }
    } finally {
      if (token !== requestToken) return
      loading.value = false
    }
  }

  watch(
    [() => orgStore.activeOrgId, () => orgStore.activeSubOrgId],
    ([nextOrgId, nextSubOrgId], [previousOrgId, previousSubOrgId]) => {
      if (nextOrgId === previousOrgId && nextSubOrgId === previousSubOrgId) return
      resetLotteriesState()
    },
  )

  const createLottery = async (payload) => {
    loading.value = true
    error.value = ''

    try {
      assertPermission(authStore, orgStore, 'createLottery', { scope: 'org' })
      const activeSubOrgId = Number(orgStore.activeSubOrgId)
      if (!Number.isInteger(activeSubOrgId) || activeSubOrgId <= 0) {
        error.value = 'Sub-organization must be selected'
        return { success: false, error: error.value }
      }

      const requestPayload = {
        ...payload,
        sub_org_id: activeSubOrgId,
      }

      const created = normalizeLotteryPayload(await createLotteryRequest(requestPayload, orgStore.activeOrgId))

      if (created?.id || created?.lottery_id) {
        lotteries.value = [created, ...lotteries.value]
        const createdId = getLotteryId(created)
        if (createdId !== null && createdId !== undefined) {
          accessibleLotteryIds.value = [String(createdId), ...accessibleLotteryIds.value.filter((id) => id !== String(createdId))]
        }
      }

      return { success: true, lottery: created }
    } catch (err) {
      if (err?.code !== RBAC_ERROR) {
        console.error('Failed to create lottery', err)
      }
      return toActionResult(err, 'Failed to create lottery')
    } finally {
      loading.value = false
    }
  }

  const fetchLotteryDetail = async (id) => {
    loading.value = true
    error.value = ''
    const previousCurrentLottery = currentLottery.value

    try {
      assertPermission(authStore, orgStore, 'viewLotteries', { scope: 'org', lotteryId: id })
      const detail = normalizeLotteryPayload(await fetchLotteryDetailRequest(id, orgStore.activeOrgId))
      if (!detail || typeof detail !== 'object') {
        error.value = 'Lottery not found or not available for your organization.'
        currentLottery.value = null
        return
      }

      currentLottery.value = detail
      syncLotteryInState(detail)

      const detailId = String(getLotteryId(detail))
      const hasDetailInList = lotteries.value.some((item) => String(getLotteryId(item)) === detailId)
      if (!hasDetailInList) {
        lotteries.value = [detail, ...lotteries.value]
      }

      await resolveAccessibleLotteryIds(lotteries.value)
    } catch (err) {
      const detailTemporarilyUnavailable = err?.code === 'LOTTERY_DETAIL_UNAVAILABLE'
      if (detailTemporarilyUnavailable) {
        const fallbackMatch = lotteries.value.find((item) => String(getLotteryId(item)) === String(id))
        if (fallbackMatch) {
          currentLottery.value = fallbackMatch
        } else {
          currentLottery.value = previousCurrentLottery
        }
        error.value = 'Lottery detail is temporarily unavailable. Showing fallback data when available.'
        return
      }

      if (err?.code === RBAC_ERROR) {
        error.value = 'Access denied'
        currentLottery.value = previousCurrentLottery
        return
      }
      console.error('Failed to fetch lottery detail', err)
      error.value = err?.response?.data?.detail ?? 'Failed to fetch lottery detail'
      currentLottery.value = previousCurrentLottery
    } finally {
      loading.value = false
    }
  }

  const fetchAssignments = async (id) => {
    loading.value = true
    error.value = ''

    try {
      assertPermission(authStore, orgStore, 'manageUsers', { scope: 'org', lotteryId: id })
      const data = await fetchLotteryAssignmentsRequest(id, orgStore.activeOrgId)
      assignments.value = Array.isArray(data) ? data : []
    } catch (err) {
      if (err?.code === RBAC_ERROR) {
        assignments.value = []
        error.value = 'Access denied'
        return
      }
      console.error('Failed to fetch assignments', err)
      error.value = err?.response?.data?.detail ?? 'Failed to fetch assignments'
    } finally {
      loading.value = false
    }
  }

  const fetchBatches = async (id) => {
    loading.value = true
    error.value = ''

    try {
      assertPermission(authStore, orgStore, 'viewBatches', { scope: 'org', lotteryId: id })
      const data = await fetchLotteryBatchesRequest(id, orgStore.activeOrgId)
      batches.value = Array.isArray(data) ? data : []
    } catch (err) {
      if (err?.code === RBAC_ERROR) {
        batches.value = []
        error.value = 'Access denied'
        return
      }
      console.error('Failed to fetch batches', err)
      error.value = err?.response?.data?.detail ?? 'Failed to fetch batches'
    } finally {
      loading.value = false
    }
  }

  const uploadTicketBatch = async (id, file, sold_tickets_json) => {
    loading.value = true
    error.value = ''

    try {
      assertPermission(authStore, orgStore, 'uploadBatch', { scope: 'org', lotteryId: id })
      const formData = new FormData()
      formData.append('file', file)
      formData.append('sold_tickets_json', sold_tickets_json)

      await uploadTicketBatchRequest(id, formData, orgStore.activeOrgId)
      return { success: true }
    } catch (err) {
      if (err?.code !== RBAC_ERROR) {
        console.error('Upload failed', err)
      }
      return toActionResult(err, 'Upload failed')
    } finally {
      loading.value = false
    }
  }

  const drawWinner = async (id) => {
    if (isDrawLocked(id)) {
      return { success: false, error: 'Draw in progress', code: 'DRAW_IN_PROGRESS' }
    }

    loading.value = true
    error.value = ''
    setDrawLocked(id, true)

    try {
      assertPermission(authStore, orgStore, 'drawWinner', { scope: 'org', lotteryId: id })
      const data = await drawWinnerRequest(id, orgStore.activeOrgId)
      if (data && typeof data === 'object') {
        syncLotteryInState(data)
      }
      return { success: true, result: data }
    } catch (err) {
      if (err?.code !== RBAC_ERROR) {
        console.error('Draw failed', err)
      }
      return toActionResult(err, 'Draw failed')
    } finally {
      setDrawLocked(id, false)
      loading.value = false
    }
  }

  const updateLottery = async (lotteryId, payload) => {
    loading.value = true
    error.value = ''

    try {
      assertPermission(authStore, orgStore, 'createLottery', { scope: 'org', lotteryId })
      const updated = await updateLotteryRequest(lotteryId, payload, orgStore.activeOrgId)
      syncLotteryInState(updated)
      return { success: true, data: updated }
    } catch (err) {
      return toActionResult(err, 'Failed to update lottery')
    } finally {
      loading.value = false
    }
  }

  const transitionLottery = async (lotteryId, request, fallbackMessage) => {
    loading.value = true
    error.value = ''

    try {
      assertPermission(authStore, orgStore, 'createLottery', { scope: 'org', lotteryId })
      const updated = await request(lotteryId, orgStore.activeOrgId)
      syncLotteryInState(updated)
      return { success: true, data: updated }
    } catch (err) {
      return toActionResult(err, fallbackMessage)
    } finally {
      loading.value = false
    }
  }

  const pauseLottery = async (lotteryId) => transitionLottery(lotteryId, pauseLotteryRequest, 'Failed to pause lottery')
  const resumeLottery = async (lotteryId) => transitionLottery(lotteryId, resumeLotteryRequest, 'Failed to resume lottery')
  const closeLottery = async (lotteryId) => transitionLottery(lotteryId, closeLotteryRequest, 'Failed to close lottery')
  const archiveLottery = async (lotteryId) => transitionLottery(lotteryId, archiveLotteryRequest, 'Failed to archive lottery')

  const fetchDrawHistory = async (id) => {
    loading.value = true
    error.value = ''

    try {
      assertPermission(authStore, orgStore, 'viewDrawHistory', { scope: 'org', lotteryId: id })
      const data = await fetchDrawHistoryRequest(id, orgStore.activeOrgId)
      drawHistory.value = Array.isArray(data) ? data : []
    } catch (err) {
      if (err?.code === RBAC_ERROR) {
        drawHistory.value = []
        error.value = 'Access denied'
        return
      }
      console.error('Failed to fetch draw history', err)
      error.value = err?.response?.data?.detail ?? 'Failed to fetch draw history'
    } finally {
      loading.value = false
    }
  }

  const fetchBatchDetail = async (lotteryId, batchId) => {
    try {
      assertPermission(authStore, orgStore, 'viewBatches', { scope: 'org', lotteryId, batchId })
      const data = await fetchBatchDetailRequest(lotteryId, batchId, orgStore.activeOrgId)
      return { success: true, data }
    } catch (err) {
      if (err?.code === RBAC_ERROR) {
        return rbacDeniedResult()
      }
      console.error('Failed to fetch batch detail', err)
      return { success: false, error: err?.response?.data?.detail ?? 'Failed to fetch batch' }
    }
  }

  const fetchBatchImage = async (lotteryId, batchId) => {
    try {
      assertPermission(authStore, orgStore, 'viewBatches', { scope: 'org', lotteryId, batchId })
      const data = await fetchBatchImageRequest(lotteryId, batchId, orgStore.activeOrgId)
      return { success: true, data }
    } catch (err) {
      if (err?.code === RBAC_ERROR) {
        return rbacDeniedResult()
      }
      console.error('Failed to fetch batch image', err)
      return { success: false, error: err?.response?.data?.detail ?? 'Failed to fetch batch image' }
    }
  }

  const fetchMyBatches = async (lotteryId) => {
    try {
      assertPermission(authStore, orgStore, 'viewBatches', { scope: 'org', lotteryId })
      const data = await fetchMyBatchesRequest(lotteryId, orgStore.activeOrgId)
      return { success: true, data: Array.isArray(data) ? data : [] }
    } catch (err) {
      if (err?.code === RBAC_ERROR) {
        return rbacDeniedResult()
      }
      console.error('Failed to fetch my batches', err)
      return { success: false, error: err?.response?.data?.detail ?? 'Failed to fetch my batches' }
    }
  }

  const fetchUserBatches = async (lotteryId, userId) => {
    try {
      assertPermission(authStore, orgStore, 'viewBatches', { scope: 'org', lotteryId, userId })
      const data = await fetchUserBatchesRequest(lotteryId, userId, orgStore.activeOrgId)
      return { success: true, data: Array.isArray(data) ? data : [] }
    } catch (err) {
      if (err?.code === RBAC_ERROR) {
        return rbacDeniedResult()
      }
      console.error('Failed to fetch user batches', err)
      return { success: false, error: err?.response?.data?.detail ?? 'Failed to fetch user batches' }
    }
  }

  const fetchBatchByTicket = async (lotteryId, ticketNumber) => {
    try {
      assertPermission(authStore, orgStore, 'viewBatches', { scope: 'org', lotteryId, ticketNumber })
      const data = await fetchBatchByTicketRequest(lotteryId, ticketNumber, orgStore.activeOrgId)
      return { success: true, data }
    } catch (err) {
      if (err?.code === RBAC_ERROR) {
        return rbacDeniedResult()
      }
      console.error('Failed to fetch batch by ticket', err)
      return { success: false, error: err?.response?.data?.detail ?? 'Failed to fetch batch by ticket' }
    }
  }

  const assignUsersToLottery = async (lotteryId, userIds) => {
    if (isAssignmentLocked(lotteryId, 'users')) {
      return { success: false, error: 'Assignment update in progress', code: 'ASSIGNMENT_IN_PROGRESS' }
    }
    if (isAssignmentBlockedByLifecycle(lotteryId)) {
      return { success: false, error: 'Assignment is not allowed for this lottery lifecycle state.', code: 'LOTTERY_READ_ONLY' }
    }

    const normalizedIds = [...new Set((Array.isArray(userIds) ? userIds : []).map((id) => Number(id)).filter((id) => Number.isInteger(id) && id > 0))]
    if (!normalizedIds.length) {
      return { success: false, error: 'No eligible users selected for assignment.' }
    }

    loading.value = true
    error.value = ''
    setAssignmentLocked(lotteryId, 'users', true)

    try {
      assertPermission(authStore, orgStore, 'assignUsers', { scope: 'org', lotteryId })
      const data = await assignUsersToLotteryRequest(lotteryId, normalizedIds, orgStore.activeOrgId)
      return { success: true, data }
    } catch (err) {
      if (err?.code !== RBAC_ERROR) {
        console.error('Failed to assign users', err)
      }
      return toActionResult(err, 'Failed to assign users')
    } finally {
      setAssignmentLocked(lotteryId, 'users', false)
      loading.value = false
    }
  }

  const assignManagersToLottery = async (lotteryId, userIds) => {
    if (isAssignmentLocked(lotteryId, 'managers')) {
      return { success: false, error: 'Assignment update in progress', code: 'ASSIGNMENT_IN_PROGRESS' }
    }
    if (isAssignmentBlockedByLifecycle(lotteryId)) {
      return { success: false, error: 'Assignment is not allowed for this lottery lifecycle state.', code: 'LOTTERY_READ_ONLY' }
    }

    const normalizedIds = [...new Set((Array.isArray(userIds) ? userIds : []).map((id) => Number(id)).filter((id) => Number.isInteger(id) && id > 0))]
    if (!normalizedIds.length) {
      return { success: false, error: 'No eligible managers selected for assignment.' }
    }

    loading.value = true
    error.value = ''
    setAssignmentLocked(lotteryId, 'managers', true)

    try {
      assertPermission(authStore, orgStore, 'assignManagers', { scope: 'org', lotteryId })
      const data = await assignManagersToLotteryRequest(lotteryId, normalizedIds, orgStore.activeOrgId)
      return { success: true, data }
    } catch (err) {
      if (err?.code !== RBAC_ERROR) {
        console.error('Failed to assign managers', err)
      }
      return toActionResult(err, 'Failed to assign managers')
    } finally {
      setAssignmentLocked(lotteryId, 'managers', false)
      loading.value = false
    }
  }

  return {
    lotteries,
    accessibleLotteryIds,
    accessibleLotteries,
    filteredLotteries,
    currentLottery,
    assignments,
    batches,
    drawHistory,
    drawLocks,
    assignmentLocks,
    loading,
    error,
    userRole,
    canAccessLottery,
    resetLotteriesState,
    fetchLotteries,
    createLottery,
    fetchLotteryDetail,
    fetchAssignments,
    fetchBatches,
    assignUsersToLottery,
    assignManagersToLottery,
    uploadTicketBatch,
    drawWinner,
    isDrawLocked,
    setDrawLocked,
    isAssignmentLocked,
    fetchDrawHistory,
    updateLottery,
    pauseLottery,
    resumeLottery,
    closeLottery,
    archiveLottery,
    fetchBatchDetail,
    fetchBatchImage,
    fetchMyBatches,
    fetchUserBatches,
    fetchBatchByTicket,
  }
})

